import { useState } from "react";
import { CATEGORIES, REPORTS, type ReportItem } from "@/mocks/reportData";

export default function Reports() {
  const [active, setActive] = useState("全部");
  const [selected, setSelected] = useState<ReportItem | null>(null);
  const featured = REPORTS.find((r) => r.featured) as ReportItem;
  const list = active === "全部" ? REPORTS.filter((r) => r.id !== featured.id) : REPORTS.filter((r) => r.category === active && r.id !== featured.id);
  const open = selected ?? featured;

  return (
    <section id="reports" className="scroll-mt-20 bg-background-50 py-16 md:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-end">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">DEEP REPORTS</span>
            <h2 className="mt-2 font-display text-2xl font-bold text-foreground-950 md:text-3xl">敢说，也要敢挖</h2>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600">
              每一条报道都来自真实线索的交叉核实。点击卡片即可展开阅读全文，也欢迎就相关话题继续补充线索。
            </p>
          </div>
        </div>

        {/* category filter */}
        <div className="mt-7 flex flex-wrap items-center gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setActive(c.key)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                active === c.key
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-secondary-100"
              }`}
            >
              <i className={`${c.icon} text-sm`} aria-hidden="true"></i>
              {c.key}
            </button>
          ))}
        </div>

        <div className="mt-7 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* featured / selected report reader */}
          <article className="lg:col-span-2">
            <div className="group cursor-pointer overflow-hidden rounded-lg bg-background-100 ring-1 ring-background-200">
              <div className="relative overflow-hidden">
                <img
                  src={open.image}
                  alt={open.title}
                  className="h-56 w-full object-cover transition-transform duration-500 group-hover:scale-105 md:h-72"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent"></div>
                <span className="absolute left-4 top-4 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
                  {open.tag}
                </span>
                <span className="absolute bottom-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-black/35 px-3 py-1 text-xs text-white backdrop-blur">
                  <i className="ri-fire-line text-xs" aria-hidden="true"></i>
                  今日热门
                </span>
              </div>
              <div className="p-5 md:p-6">
                <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-price-tag-3-line" aria-hidden="true"></i>
                    {open.category}
                  </span>
                  <span>{open.date}</span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-eye-line" aria-hidden="true"></i>
                    {open.views}
                  </span>
                  <span>{open.length}</span>
                </div>
                <h3 className="mt-3 font-display text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                  {open.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{open.summary}</p>
                <div className="mt-4 space-y-3 border-t border-background-200 pt-4">
                  {open.body.map((p) => (
                    <p key={p} className="text-[15px] leading-7 text-foreground-800">
                      {p}
                    </p>
                  ))}
                </div>
                <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
                  <span className="inline-flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-pen-nib-line text-sm text-primary-500" aria-hidden="true"></i>
                    {open.author}
                  </span>
                  <a
                    href="#channels"
                    className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
                  >
                    我有相关线索
                    <i className="ri-arrow-right-up-line text-sm" aria-hidden="true"></i>
                  </a>
                </div>
              </div>
            </div>
          </article>

          {/* report list */}
          <div className="flex flex-col gap-4">
            <h3 className="font-display text-base font-bold text-foreground-950">更多相关报道</h3>
            {list.length === 0 && (
              <p className="rounded-lg bg-background-100 p-4 text-sm text-foreground-600">
                该栏目暂无更多报道，欢迎通过线索入口补充。
              </p>
            )}
            {list.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setSelected(r)}
                className={`group flex flex-col rounded-lg p-4 text-left ring-1 transition-colors ${
                  selected?.id === r.id
                    ? "bg-background-100 ring-primary-300"
                    : "bg-background-50 ring-background-200 hover:bg-background-100 hover:ring-primary-200"
                }`}
              >
                <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                  <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-900">{r.category}</span>
                  {r.hot && (
                    <span className="inline-flex items-center gap-0.5 text-primary-600">
                      <i className="ri-fire-fill text-xs" aria-hidden="true"></i>
                      热
                    </span>
                  )}
                  <span>{r.date}</span>
                </div>
                <h4 className="mt-2 line-clamp-2 font-display text-[15px] font-semibold leading-snug text-foreground-900 group-hover:text-primary-700">
                  {r.title}
                </h4>
                <p className="mt-1.5 line-clamp-2 text-sm text-foreground-600">{r.summary}</p>
                <div className="mt-3 flex items-center justify-between text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-eye-line" aria-hidden="true"></i>
                    {r.views}
                  </span>
                  <span className="inline-flex items-center gap-1 text-primary-600">
                    展开阅读
                    <i className="ri-arrow-right-line text-xs" aria-hidden="true"></i>
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}